import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.png";
import { FaYoutube, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";

const Footer = () => {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("modal") === "request-account-deletion") {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [location.search]);

  const openModal = () => {
    const params = new URLSearchParams(location.search);
    params.set("modal", "request-account-deletion");
    navigate({ search: params.toString() }, { replace: false });
  };

  const closeModal = () => {
    const params = new URLSearchParams(location.search);
    params.delete("modal");
    navigate({ search: params.toString() }, { replace: false });
    setEmail("");
    setPhone("");
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-]{10,}$/;

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!phone) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        "https://us-central1-timeclock-f2c1a.cloudfunctions.net/deleteAccount",
        { email, phone }
      );

      if (response.status === 200) {
        setToastMessage("Account deletion request submitted successfully");
        setShowToast(true);
        closeModal();
        setTimeout(() => setShowToast(false), 3000);
      }
    } catch (error) {
      setToastMessage("Failed to submit request. Please try again later.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-darkgray to-[#0a101f] text-white relative overflow-hidden">
      {/* Decorative gradient orb */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 pt-16 pb-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <img src={logo} alt="ClockIn Master Logo" className="w-9 h-9" />
              <span className="text-xl font-bold tracking-tight">ClockIn Master</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Your all-in-one platform for workforce management, time tracking, and payroll simplified.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.youtube.com/@ClockInMaster"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="YouTube"
              >
                <FaYoutube className="text-lg" />
              </a>
              <a
                href="mailto:hello@clockinmaster.com"
                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Email"
              >
                <FaEnvelope className="text-lg" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h5 className="text-sm font-semibold tracking-wider uppercase text-gray-300 mb-5">Product</h5>
            <ul className="space-y-3">
              {[
                { label: "Features", href: "#features", scroll: true },
                { label: "Video Tutorials", href: "#video-tutorials", scroll: true },
                { label: "FAQ", to: "/faq" },
                { label: "Contact", href: "#contact", scroll: true },
              ].map((item) => (
                <li key={item.label}>
                  {item.to ? (
                    <Link to={item.to} className="text-gray-400 hover:text-white text-sm transition-colors duration-300">
                      {item.label}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      className="text-gray-400 hover:text-white text-sm transition-colors duration-300"
                      onClick={(e) => {
                        if (item.scroll) {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          setTimeout(() => {
                            document.getElementById(item.href.slice(1))?.scrollIntoView({ behavior: "smooth" });
                          }, 100);
                        }
                      }}
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h5 className="text-sm font-semibold tracking-wider uppercase text-gray-300 mb-5">Legal</h5>
            <ul className="space-y-3">
              <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors duration-300">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors duration-300">Privacy Policy</Link></li>
              <li><Link to="/support" className="text-gray-400 hover:text-white text-sm transition-colors duration-300">Support Policy</Link></li>
              <li>
                <button
                  className="text-gray-400 hover:text-white text-sm transition-colors duration-300 bg-transparent border-none p-0 cursor-pointer"
                  onClick={openModal}
                  type="button"
                >
                  Request Account Deletion
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h5 className="text-sm font-semibold tracking-wider uppercase text-gray-300 mb-5">Contact</h5>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <FaEnvelope className="mt-1 flex-shrink-0 text-secondary" />
                <a href="mailto:hello@clockinmaster.com" className="hover:text-white transition-colors">hello@clockinmaster.com</a>
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <FaMapMarkerAlt className="mt-1 flex-shrink-0 text-secondary" />
                <span>71-75 Shelton Street, Covent Garden, London, WC2H 9JQ</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-gray-500 text-sm">
            © {new Date().getFullYear()} ClockIn Master. All rights reserved.
          </span>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
          </div>
        </div>
      </div>

      {/* Account Deletion Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-card-hover animate-fadeInUp">
            <h3 className="text-xl font-bold text-primary mb-2">Request Account Deletion</h3>
            <p className="text-muted text-sm mb-6">Enter your details below and we'll process your request.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2.5 border-2 rounded-xl text-sm transition-all duration-300 ${errors.email ? "border-red-400" : "border-gray-200 focus:border-secondary"
                    } focus:ring-2 focus:ring-secondary/10 outline-none`}
                  placeholder="Enter your email"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`w-full px-4 py-2.5 border-2 rounded-xl text-sm transition-all duration-300 ${errors.phone ? "border-red-400" : "border-gray-200 focus:border-secondary"
                    } focus:ring-2 focus:ring-secondary/10 outline-none`}
                  placeholder="Enter your phone number"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 text-sm text-gray-500 hover:text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary text-sm rounded-xl disabled:opacity-50"
                >
                  {isLoading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 bg-accent text-primary px-6 py-3 rounded-xl shadow-lg z-50 text-sm font-semibold animate-fadeInUp">
          {toastMessage}
        </div>
      )}
    </footer>
  );
};

export default Footer;